import type { JsError } from '@feedback/shared';
import { RingBuffer } from './ring-buffer.js';

const MAX_ERRORS = 25;
const buffer = new RingBuffer<JsError>(MAX_ERRORS);
let installed = false;
let pageLoadedAt = Date.now();

function describeRejectionReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return reason.stack
      ? { message: `${reason.name}: ${reason.message}`, stack: reason.stack }
      : { message: `${reason.name}: ${reason.message}` };
  }
  if (typeof reason === 'string') return { message: reason };
  try {
    return { message: JSON.stringify(reason) ?? String(reason) };
  } catch {
    return { message: 'Unserialisable rejection reason' };
  }
}

/**
 * Captures uncaught errors and unhandled promise rejections. Listeners are
 * passive — they never call `preventDefault()`, so the host page's own error
 * handling and reporting continue to see everything unchanged.
 */
export function installErrorCollector(): void {
  if (installed) return;
  installed = true;
  pageLoadedAt = Date.now();

  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      try {
        buffer.push({
          kind: 'error',
          message: (event.message || 'Unknown error').slice(0, 2_000),
          ...(event.error instanceof Error && event.error.stack
            ? { stack: event.error.stack.slice(0, 8_000) }
            : {}),
          ...(event.filename ? { source: event.filename.slice(0, 500) } : {}),
          ...(typeof event.lineno === 'number' ? { line: event.lineno } : {}),
          ...(typeof event.colno === 'number' ? { column: event.colno } : {}),
          at: Date.now() - pageLoadedAt,
        });
      } catch {
        /* never interfere with the host page */
      }
    },
    // Capture phase so we see errors even if something downstream stops propagation.
    { capture: true, passive: true },
  );

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      try {
        const { message, stack } = describeRejectionReason(event.reason);
        buffer.push({
          kind: 'unhandledrejection',
          message: message.slice(0, 2_000),
          ...(stack ? { stack: stack.slice(0, 8_000) } : {}),
          at: Date.now() - pageLoadedAt,
        });
      } catch {
        /* never interfere with the host page */
      }
    },
    { capture: true, passive: true },
  );
}

export function getJsErrors(): JsError[] {
  return buffer.toArray();
}
